import {ArrowLeftIcon, ArrowRightIcon} from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, {ReactElement, useCallback, useEffect, useRef} from 'react';

import Col from '../../common/Col';
import Row from '../../common/Row';
import {useBreakpoint} from '../../common/util/useBreakpoint';
import {Walkthrough} from '../types';
import NormalButton from './buttons/NormalButton';
import Modal, {ModalHandle} from './Modal';

const HIGHLIGHTED_Z_INDEX = 'z-[11]';

type WalkthroughProps = {
  walkthrough?: Walkthrough;
  onSkipButtonClick: () => void;
  onContinueButtonClick: () => void;
  onBackButtonClick: () => void;
};

const Walkthrough = ({
  walkthrough,
  onSkipButtonClick,
  onContinueButtonClick,
  onBackButtonClick,
}: WalkthroughProps): ReactElement => {
  const {isMobile} = useBreakpoint('mobile');
  const modalRef = useRef<ModalHandle>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modalRef.current) return;
    if (walkthrough) {
      modalRef.current.open();
    } else {
      modalRef.current.close();
    }
  }, [walkthrough, modalRef]);

  const highlightElement = useCallback(() => {
    if (!walkthrough?.highlightElementWithId) return;
    const highlightedElement = document.getElementById(walkthrough.highlightElementWithId);
    if (highlightedElement) {
      highlightedElement.classList.add(HIGHLIGHTED_Z_INDEX);
      highlightedElement.classList.add('pointer-events-none');
      if (walkthrough.highlightedClassname) {
        walkthrough.highlightedClassname.split(' ').forEach(c => highlightedElement.classList.add(c));
      }
    }
  }, [walkthrough?.highlightElementWithId, walkthrough?.highlightedClassname]);

  const lowlightElement = useCallback(() => {
    if (!walkthrough?.highlightElementWithId) return;
    const highlightedElement = document.getElementById(walkthrough.highlightElementWithId);
    if (highlightedElement) {
      highlightedElement.classList.remove(HIGHLIGHTED_Z_INDEX);
      highlightedElement.classList.remove('pointer-events-none');
      if (walkthrough.highlightedClassname) {
        walkthrough.highlightedClassname.split(' ').forEach(c => highlightedElement.classList.remove(c));
      }
    }
  }, [walkthrough?.highlightElementWithId, walkthrough?.highlightedClassname]);

  const handleContinueButtonClick = () => {
    lowlightElement();
    contentRef.current?.scrollTo({top: 0});
    onContinueButtonClick();
  };

  const handleBackButtonClick = () => {
    lowlightElement();
    contentRef.current?.scrollTo({top: 0});
    onBackButtonClick();
  };

  const handleSkipButtonClick = () => {
    lowlightElement();
    contentRef.current?.scrollTo({top: 0});
    onSkipButtonClick();
  };

  const bottomButtons = (
    <Row className={classNames('px-xl justify-between gap-sm', !walkthrough?.canGoBack && 'flex-row-reverse')}>
      {walkthrough?.canGoBack && (
        <NormalButton onClick={handleBackButtonClick} light>
          <Row className={classNames('font-medium justify-between items-center', {'w-[25rem]': !isMobile})}>
            <ArrowLeftIcon className="w-sm h-sm" />
            {!isMobile && (
              <>
                Back
                <div className="w-sm" />
              </>
            )}
          </Row>
        </NormalButton>
      )}
      <NormalButton onClick={handleContinueButtonClick}>
        <Row className={classNames('font-medium justify-between items-center', {'w-[25rem]': !isMobile})}>
          {!isMobile && <div className="w-sm" />}
          Continue
          <ArrowRightIcon className="w-sm h-sm ml-sm" />
        </Row>
      </NormalButton>
    </Row>
  );

  return (
    <Modal
      ref={modalRef}
      beforeOpen={() => highlightElement()}
      afterClose={() => lowlightElement()}
      hideCloseButton
      closeOnClickOutside={false}
      bottom={bottomButtons}
      maxHeightScreen={isMobile}
    >
      <button
        className="absolute right-lg top-md text-primary-500 font-medium mobile:text-sm"
        onClick={handleSkipButtonClick}
      >
        SKIP
      </button>
      <Col className={classNames('pt-md space-y-xs', isMobile ? 'h-full' : 'px-lg')}>
        <h1 className="font-semibold text-3xl mobile:text-2xl">{walkthrough?.title}</h1>
        <p
          ref={contentRef}
          className={classNames(
            'font-medium text-xl whitespace-pre-wrap mobile:text-base',
            isMobile && 'min-h-0 overflow-auto'
          )}
        >
          {walkthrough?.contentLabel}
        </p>
      </Col>
    </Modal>
  );
};

export default Walkthrough;
